import {
  computeStudentKey,
  emptyStudentStats,
  ReportStats,
  StudentInfo,
  StudentStats,
  StudentStatsPerScreen,
} from "./reports-model"
import { hasText } from "utils/helpers"
import { duration } from "moment"
import { Report, ReportedAnswer, ScreenType } from "models/tables"

function percent(fraction: number, whole: number) {
  if (whole != 0) {
    return (100 * fraction) / whole
  } else {
    return 0
  }
}

export function computeStatsForStudent(
  report: Report,
  student: StudentInfo
): StudentStats {
  const stats: StudentStats = emptyStudentStats()
  stats.name = student.name
  stats.userId = student.userId || null

  computeStatsForPlayerInternal(report, stats)

  return stats
}

function answerIsForStudent(
  answer: ReportedAnswer,
  student: StudentStats
): boolean {
  if (hasText(answer.userId) && hasText(student.userId)) {
    return answer.userId! === student.userId!
  } else {
    return answer.studentName === student.name
  }
}

function computeStatsForPlayerInternal(report: Report, student: StudentStats) {
  student.reportId = report.id

  student.totalCountScreensThatCanHaveAnswers = 0
  for (let screen of report.screens) {
    if (screen.type !== ScreenType.Presentation) {
      student.totalCountScreensThatCanHaveAnswers++
    }
  }

  for (let playedScreen of report.playedScreens) {
    let lessonScreen = report.screens[playedScreen.screenIndex]
    if (!lessonScreen && playedScreen.originalScreenId) {
      lessonScreen = report.screens.find(x => x.id === playedScreen.originalScreenId)!
    }
    
    const studentScreen: StudentStatsPerScreen = {
      id: playedScreen.id,
      durationText: "",
      screen: lessonScreen,
      ordinal: playedScreen.screenIndex,
      isCorrect: false,
      timeStartedText: playedScreen.timeStarted,
      countIncorrectAnswers: 0,
      countCorrectAnswers: 0,
      countUnattemptedAnswers: 0,
      points: 0,
      answers: [],
    }
    student.screens.push(studentScreen)

    const screenDurations = Array<string>()

    let isScreenUnattempted = true

    for (let answer of playedScreen.answers) {
      if (!answerIsForStudent(answer, student)) {
        continue
      }

      screenDurations.push(answer.timeToComplete)
      studentScreen.answers.push(answer)
      if (answer.isCompleted) {
        if (answer.isCorrect) {
          isScreenUnattempted = false
          studentScreen.countCorrectAnswers++
        } else {
          isScreenUnattempted = false
          studentScreen.countIncorrectAnswers++
        }
      } else {
        studentScreen.countUnattemptedAnswers++
      }
      studentScreen.points += answer.points
      student.totalPoints += answer.points
    }

    if (isScreenUnattempted) {
      student.countUnattemptedScreens++
    } else {
      studentScreen.isCorrect =
        studentScreen.countCorrectAnswers / studentScreen.answers.length >= 0.5
      if (studentScreen.isCorrect) {
        student.countCorrectScreens++
      } else {
        student.countIncorrectScreens++
      }
    }

    if (screenDurations.length > 0) {
      screenDurations.sort(
        (a, b) => duration(b).asMilliseconds() - duration(a).asMilliseconds()
      )
      studentScreen.durationText = screenDurations[0]
    } else {
      studentScreen.durationText = playedScreen.timeToComplete
    }
  }

  student.percentCorrectScreens = percent(
    student.countCorrectScreens,
    student.totalCountScreensThatCanHaveAnswers
  )
  student.percentIncorrectScreens = percent(
    student.countIncorrectScreens,
    student.totalCountScreensThatCanHaveAnswers
  )
  student.percentUnattemptedScreens = percent(
    student.countUnattemptedScreens,
    student.totalCountScreensThatCanHaveAnswers
  )

  student.successRate = student.percentCorrectScreens
}

export default function reportsComputeStats(report: Report) {
  const stats: ReportStats = {
    report: report,
    students: [],
  }

  // insert empty player
  for (let screen of report.playedScreens) {
    for (let answer of screen.answers) {
      if (!stats.students.find((x) => answerIsForStudent(answer, x))) {
        const playerStats = emptyStudentStats()
        playerStats.userId = answer.userId
        playerStats.name = answer.studentName
        playerStats.key = computeStudentKey(playerStats)

        stats.students.push(playerStats)
      }
    }
  }

  // compute stats per player
  for (let student of stats.students) {
    computeStatsForPlayerInternal(report, student)
  }

  stats.students.sort(
    (a, b) =>
      // first - by % correct answers, then by points
      (b.percentCorrectScreens - a.percentCorrectScreens) * 100_000 +
      (b.totalPoints - a.totalPoints)
  )

  if (stats.students.length != 0) {
    let totalSuccessRate = 0
    for (let student of stats.students) {
      totalSuccessRate += student.successRate
    }

    stats.report.successRate = percent(totalSuccessRate, stats.students.length)
  }

  // console.log(stats);

  return stats
}
